import type { ProviderKind, ServerProvider } from "@cmmd-forge/contracts";
import { memo } from "react";
import { Clock3Icon, StarIcon } from "lucide-react";

import type { Icon } from "../Icons";
import { ScrollArea } from "../ui/scroll-area";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "~/lib/utils";

import { providerKindForRail, type PickerRailProvider } from "../../cmmd/providerAvailabilityJoin";
import { PICKER_PROVIDER_DISPLAY_NAME, PICKER_PROVIDER_ICON_BY_PROVIDER } from "./ModelListRow";

const SELECTED_BUTTON_CLASS = "bg-background text-foreground shadow-sm";
const SELECTED_INDICATOR_CLASS =
  "pointer-events-none absolute -right-1 top-1/2 z-10 h-5 w-0.5 -translate-y-1/2 rounded-l-full bg-primary";
const SOON_BADGE_CLASS =
  "pointer-events-none absolute -right-0.5 top-0.5 z-10 flex size-3.5 items-center justify-center rounded-full bg-transparent shadow-sm text-muted-foreground";
const SIGN_IN_BADGE_CLASS =
  "pointer-events-none absolute -right-0.5 -top-0.5 z-10 size-2 rounded-full bg-warning ring-2 ring-background";
const PICKER_TOOLTIP_SIDE = "left" as const;
const PICKER_TOOLTIP_CLASS = "max-w-64 text-balance font-normal leading-snug";

export type SidebarProviderEntry = {
  readonly provider: PickerRailProvider;
  readonly available: boolean;
  /**
   * The snapshot's status, or `"unknown"` when no snapshot spoke for this row.
   * `"unknown"` is the picker's own sentinel, not a `ServerProvider` status, and
   * it keeps the row selectable rather than reporting a verdict nobody gave.
   */
  readonly status: ServerProvider["status"] | "unknown";
  readonly statusMessage?: string;
  /** The catalog is visible for inspection, but its models cannot be selected. */
  readonly inspectable?: boolean;
  /**
   * The one sentence this row shows, in the four-reason vocabulary of
   * `CONTEXT.md` section "Provider availability" or one of the two the Shell
   * adds when it has no verdict yet. It is what the accessible label and the
   * tooltip both carry.
   *
   * Always present for a Withheld Provider. Not a refusal on its own: a row
   * that is still waiting on provider access carries "Checking provider
   * access." and stays selectable.
   */
  readonly sentence?: string;
  /**
   * A roadmap row. Draws the clock badge, and is the only row whose sentence
   * may be "Coming soon." — set from the withheld reason, never from a gap in
   * the payload.
   */
  readonly comingSoon?: boolean;
  /**
   * Deliberately a separate axis from `status`. A provider can be perfectly
   * `ready` — installed, enabled, reachable — and still hold no credential, so
   * folding this into `status` would lose exactly the case it exists for: the
   * user picks the default provider, sends, and the turn dies seconds later on
   * a missing credential the picker never mentioned.
   */
  readonly requiresSignIn?: boolean;
};

/** A snapshot arrived and says the provider can be picked right now. */
function isProviderReady(entry: SidebarProviderEntry): boolean {
  return entry.available && entry.status === "ready";
}

/**
 * Whether the rail entry stays clickable.
 *
 * "unknown" is the picker's own sentinel for "no snapshot has arrived for this
 * target yet" — it is not a `ServerProvider` status and it is not evidence
 * against the provider. Greying the rail out on it repeats the mistake #2052
 * fixed one level up: the control whose job is "pick a provider that works",
 * switched off by not yet knowing which one does. Availability still gates;
 * only the verdict-free state stays selectable.
 */
function isProviderSelectable(entry: SidebarProviderEntry): boolean {
  return isProviderReady(entry) || (entry.available && entry.status === "unknown");
}

/** Verdict word for a provider that reported something other than "ready". */
function unavailableVerdict(status: SidebarProviderEntry["status"]): string {
  switch (status) {
    case "error":
      return "Unavailable";
    case "warning":
      return "Limited";
    case "disabled":
      return "Disabled in settings";
    default:
      return "Not ready";
  }
}

function describeReadyProvider(entry: SidebarProviderEntry, label: string): string {
  if (!entry.requiresSignIn) return label;
  // Only reported for an otherwise-ready provider. A disabled or errored one
  // has a more fundamental problem, and sending that user to a sign-in flow
  // that cannot help them would be worse than saying nothing.
  const message = entry.statusMessage?.trim();
  return message
    ? `${label} — Sign-in required. ${message}`
    : `${label} — Sign-in required. Connect this provider in Settings, or pick one that is already connected.`;
}

/**
 * Build the hover tooltip for a sidebar provider button. Mirrors t3code's
 * describeUnavailableInstance — when the provider isn't `ready`, append the
 * snapshot's status message (if any) after a dash.
 */
function describeProvider(entry: SidebarProviderEntry): string {
  const label = PICKER_PROVIDER_DISPLAY_NAME[entry.provider];
  if (entry.sentence) return `${label}. ${entry.sentence}`;
  if (entry.status === "ready") return describeReadyProvider(entry, label);

  const message = entry.statusMessage?.trim();
  // No snapshot has arrived for this target yet. Reporting "Not ready." would
  // state a verdict we do not have, on a provider that is very likely fine.
  if (entry.status === "unknown") return message ? `${label} — ${message}` : label;

  const kind = unavailableVerdict(entry.status);
  return message ? `${label} — ${kind}. ${message}` : `${label} — ${kind}.`;
}

function ProviderRailButton(props: {
  readonly entry: SidebarProviderEntry;
  readonly isSelected: boolean;
  readonly isSelectable: boolean;
  readonly canOpen: boolean;
  readonly tooltip: string;
  readonly onSelectProvider: (provider: ProviderKind) => void;
}) {
  const ProviderIcon: Icon | undefined = PICKER_PROVIDER_ICON_BY_PROVIDER[props.entry.provider];

  return (
    <button
      data-model-picker-provider={props.entry.provider}
      data-testid={`model-picker-provider-${props.entry.provider}`}
      className={cn(
        "relative isolate flex min-h-touch w-full cursor-pointer aspect-square items-center justify-center rounded transition-colors hover:bg-muted md:min-h-0",
        props.isSelected ? SELECTED_BUTTON_CLASS : null,
        props.isSelectable ? null : "opacity-50",
        props.canOpen ? null : "cursor-not-allowed hover:bg-transparent",
      )}
      onClick={() => {
        // A roadmap row has no `ProviderKind` behind it, so there is nothing to
        // select. It is disabled as well; this keeps the two agreeing.
        const providerKind = providerKindForRail(props.entry.provider);
        if (props.canOpen && providerKind !== null) props.onSelectProvider(providerKind);
      }}
      disabled={!props.canOpen}
      type="button"
      aria-label={props.tooltip}
      data-model-selection-disabled={props.isSelectable ? undefined : "true"}
    >
      {ProviderIcon ? <ProviderIcon className="size-5" aria-hidden /> : null}
    </button>
  );
}

function ProviderComingSoonBadge(props: { readonly visible: boolean }) {
  return props.visible ? (
    <span className={SOON_BADGE_CLASS} aria-hidden>
      <Clock3Icon className="size-2" />
    </span>
  ) : null;
}

function ProviderSignInBadge(props: {
  readonly provider: PickerRailProvider;
  readonly visible: boolean;
}) {
  return props.visible ? (
    <span
      data-testid={`model-picker-provider-signin-${props.provider}`}
      className={SIGN_IN_BADGE_CLASS}
      aria-hidden
    />
  ) : null;
}

function ModelPickerProviderButton(props: {
  readonly entry: SidebarProviderEntry;
  readonly isSelected: boolean;
  readonly onSelectProvider: (provider: ProviderKind) => void;
}) {
  const { entry } = props;
  const isReady = isProviderReady(entry);
  const isSelectable = isProviderSelectable(entry);
  const canOpen = isSelectable || entry.inspectable === true;
  const tooltip = describeProvider(entry);

  const button = (
    <ProviderRailButton
      entry={entry}
      isSelected={props.isSelected}
      isSelectable={isSelectable}
      canOpen={canOpen}
      tooltip={tooltip}
      onSelectProvider={props.onSelectProvider}
    />
  );

  return (
    <div className="relative w-full">
      {props.isSelected ? <div className={SELECTED_INDICATOR_CLASS} /> : null}
      <ProviderComingSoonBadge visible={entry.comingSoon === true} />
      <ProviderSignInBadge
        provider={entry.provider}
        visible={isReady && entry.requiresSignIn === true}
      />
      <Tooltip>
        <TooltipTrigger
          render={canOpen ? button : <span className="relative block w-full">{button}</span>}
        />
        <TooltipPopup side={PICKER_TOOLTIP_SIDE} align="center" className={PICKER_TOOLTIP_CLASS}>
          {tooltip}
        </TooltipPopup>
      </Tooltip>
    </div>
  );
}

export const ModelPickerSidebar = memo(function ModelPickerSidebar(props: {
  selectedProvider: ProviderKind | "favorites";
  onSelectProvider: (provider: ProviderKind | "favorites") => void;
  providers: ReadonlyArray<SidebarProviderEntry>;
  showFavorites?: boolean;
}) {
  const showFavorites = props.showFavorites ?? true;

  return (
    <ScrollArea
      hideScrollbars
      scrollFade
      className="w-touch shrink-0 bg-muted/30 md:w-12"
      data-model-picker-sidebar="true"
    >
      <div className="flex min-h-full flex-col gap-2 px-0 py-1 md:gap-1 md:p-1">
        {showFavorites ? (
          <div className="mb-2 border-b pb-2 md:mb-1 md:pb-1">
            <div className="relative w-full">
              {props.selectedProvider === "favorites" && (
                <div className={SELECTED_INDICATOR_CLASS} />
              )}
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      className={cn(
                        "relative isolate flex min-h-touch w-full cursor-pointer aspect-square items-center justify-center rounded transition-colors hover:bg-muted md:min-h-0",
                        props.selectedProvider === "favorites" && SELECTED_BUTTON_CLASS,
                      )}
                      onClick={() => props.onSelectProvider("favorites")}
                      type="button"
                      data-model-picker-provider="favorites"
                      aria-label="Favorites"
                    >
                      <StarIcon className="size-5 fill-current shrink-0" aria-hidden />
                    </button>
                  }
                />
                <TooltipPopup
                  side={PICKER_TOOLTIP_SIDE}
                  align="center"
                  className={PICKER_TOOLTIP_CLASS}
                >
                  Favorites
                </TooltipPopup>
              </Tooltip>
            </div>
          </div>
        ) : null}

        {props.providers.map((entry) => (
          <ModelPickerProviderButton
            key={entry.provider}
            entry={entry}
            isSelected={props.selectedProvider === entry.provider}
            onSelectProvider={props.onSelectProvider}
          />
        ))}
      </div>
    </ScrollArea>
  );
});
